
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Flag, Home, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthModalLoginForm } from "@/components/auth/AuthModalLoginForm";
import { AuthModalDivider } from "@/components/auth/AuthModalDivider";
import { useToast } from "@/hooks/use-toast";
import BackButton from "@/components/ui/back-button";

const CompanyLogin = () => {
  const { user, loading: authLoading, isCompany } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Parse the redirect path from the location
  const searchParams = new URLSearchParams(location.search);
  const fromParam = searchParams.get('from');
  
  // Get the redirect path from various sources with priority
  const from = location.state?.from || fromParam || "/company-dashboard";
  console.log("CompanyLogin - Redirect path:", from);
  
  // Handle initial auto-redirect if user is already logged in as a company account
  useEffect(() => {
    if (!authLoading && user && isCompany) {
      console.log("CompanyLogin - User is already logged in as a company account, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [user, authLoading, isCompany, navigate, from]);
  
  // Handle redirect after successful login action
  useEffect(() => {
    if (!authLoading && user && shouldRedirect) {
      console.log("CompanyLogin - Login success triggered, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from, shouldRedirect]);

  const handleLoginSuccess = () => {
    console.log("CompanyLogin - Login success handler called, setting shouldRedirect flag");
    setShouldRedirect(true);
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const handleToggleSignup = () => {
    // Preserve the redirect target when switching to signup
    navigate('/company-signup', { state: { from } });
  };

  // If auth is loading, show minimal UI
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading authentication...</p>
      </div>
    );
  }

  // If user is already logged in AND we've triggered a redirect, show minimal UI
  if (user && shouldRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Redirecting to company dashboard...</p>
      </div>
    );
  }

  // Main login form UI
  return (
    <div className="min-h-screen flex flex-col">
      <div className="absolute top-4 left-4 z-10">
        <BackButton />
      </div>
      
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-3">
              <div className="p-2 bg-redflags-red/10 rounded-full">
                <Building className="w-8 h-8 text-redflags-red" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-redflags-dark">
              Company Login
            </h1>
            <p className="text-redflags-gray mt-2">
              Log in to manage your company profile
            </p>
            {from !== "/company-dashboard" && (
              <p className="text-sm text-redflags-red mt-2">
                Sign in to continue to your previous action
              </p>
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <AuthModalLoginForm onSuccess={handleLoginSuccess} onClose={() => {}} />
            <AuthModalDivider />
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              <span>Don't have a company account?</span>{' '}
              <Button variant="link" className="p-0 h-auto" onClick={handleToggleSignup}>
                Register your company
              </Button>
            </p>
          </div>
          
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
              onClick={handleReturnHome}
            >
              <Home className="w-4 h-4" />
              Return to Home
            </Button>
          </div>
          
          <footer className="mt-10 pt-6 text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} RedFlags. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default CompanyLogin;
